import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Package, Truck, Loader2, Sparkles, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OrderSuccessPage = () => {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrder();
  }, [orderId]);

  const fetchOrder = async () => {
    try {
      const response = await axios.get(`${API}/orders/${orderId}`);
      setOrder(response.data);
    } catch (error) {
      console.error('Error fetching order:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <Loader2 className="h-12 w-12 animate-spin text-amber-600" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-stone-50">
        <Sparkles className="h-16 w-16 text-stone-300 mb-4" />
        <h1 className="text-4xl font-black mb-4 text-stone-900">Order Not Found</h1>
        <Link to="/">
          <Button className="bg-amber-600 hover:bg-amber-700 rounded-none">Go Home</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50" data-testid="order-success-page">
      {/* Success Header with Kilim Pattern */}
      <div className="bg-gradient-to-r from-amber-700 via-red-700 to-amber-700 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `
              repeating-linear-gradient(45deg, transparent, transparent 20px, white 20px, white 22px),
              repeating-linear-gradient(-45deg, transparent, transparent 20px, white 20px, white 22px)
            `
          }}
        ></div>
        <div className="max-w-[800px] mx-auto px-4 sm:px-6 lg:px-8 py-12 relative z-10 text-center">
          <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-6 shadow-xl">
            <CheckCircle2 className="h-12 w-12 text-green-600" />
          </div>
          <h1 className="text-4xl md:text-5xl font-black text-white mb-2">Order Confirmed!</h1>
          <p className="text-xl text-amber-100">Thank you for choosing Amlaria</p>
        </div>
        <div className="h-2"
          style={{
            backgroundImage: `repeating-linear-gradient(90deg, 
              #78350f 0px, #78350f 30px, 
              #fbbf24 30px, #fbbf24 60px, 
              #dc2626 60px, #dc2626 90px)`
          }}
        ></div>
      </div>

      <div className="max-w-[800px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Order Details */}
        <div className="bg-white shadow-xl p-8 mb-6 border-t-4 border-amber-600" data-testid="order-details">
          <div className="border-b border-stone-200 pb-6 mb-6">
            <div className="flex justify-between items-center mb-2">
              <span className="text-stone-600">Order Number:</span>
              <span className="font-black text-lg text-stone-900" data-testid="order-number">{order.order_number}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-stone-600">Order Date:</span>
              <span className="font-semibold text-stone-900">
                {new Date(order.created_at).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </span>
            </div>
          </div>

          {/* Items */}
          <div className="mb-6">
            <h3 className="font-black text-lg mb-4 text-stone-900">Order Items</h3>
            <div className="space-y-3">
              {order.items.map((item, index) => (
                <div key={index} className="flex justify-between py-2 border-b border-stone-100">
                  <span className="text-stone-700">
                    {item.product_name} × {item.quantity}
                  </span>
                  <span className="font-semibold text-stone-900">${(item.price * item.quantity).toFixed(2)}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Totals */}
          <div className="border-t border-stone-200 pt-4 space-y-2">
            <div className="flex justify-between">
              <span className="text-stone-600">Subtotal:</span>
              <span className="font-semibold text-stone-900">${order.subtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-stone-600">Tax:</span>
              <span className="font-semibold text-stone-900">${order.tax.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-stone-600">Shipping:</span>
              <span className="font-semibold text-stone-900">
                {order.shipping === 0 ? <span className="text-green-600">FREE</span> : `$${order.shipping.toFixed(2)}`}
              </span>
            </div>
            <div className="border-t border-stone-200 pt-3 flex justify-between text-xl font-black">
              <span className="text-stone-900">Total:</span>
              <span className="text-stone-900">${order.total.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Shipping Info */}
        <div className="bg-white shadow-xl p-8 mb-6 border-l-4 border-amber-600" data-testid="shipping-info">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-amber-100 flex items-center justify-center">
              <Truck className="h-5 w-5 text-amber-700" />
            </div>
            <h3 className="font-black text-lg text-stone-900">Shipping Information</h3>
          </div>
          <div className="text-stone-700">
            <p>{order.shipping_address.street}</p>
            <p>
              {order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.zip}
            </p>
            <p>{order.shipping_address.country}</p>
          </div>
          {order.tracking_number && (
            <div className="mt-4 p-4 bg-stone-100">
              <p className="text-sm text-stone-600 mb-1">Tracking Number:</p>
              <p className="font-mono font-bold text-stone-900">{order.tracking_number}</p>
            </div>
          )}
        </div>

        {/* What's Next */}
        <div className="bg-amber-50 border border-amber-200 p-6 mb-6" data-testid="whats-next">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-amber-100 flex items-center justify-center flex-shrink-0">
              <Package className="h-5 w-5 text-amber-700" />
            </div>
            <div>
              <h3 className="font-black text-lg mb-3 text-stone-900">What happens next?</h3>
              <ul className="space-y-2 text-stone-700">
                <li className="flex items-start gap-2">
                  <span className="text-amber-600 font-bold">1.</span>
                  You'll receive an email confirmation shortly
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-600 font-bold">2.</span>
                  We'll send you tracking information once your order ships
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-600 font-bold">3.</span>
                  Estimated delivery: 2-3 business days
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-600 font-bold">4.</span>
                  Questions? Contact our support team anytime
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-4">
          <Link to="/" className="flex-1">
            <Button className="w-full bg-amber-600 hover:bg-amber-700 rounded-none py-6" size="lg" data-testid="continue-shopping-btn">
              Continue Shopping
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
          <Button 
            variant="outline" 
            size="lg" 
            className="flex-1 rounded-none border-stone-300 py-6" 
            onClick={() => window.print()}
            data-testid="print-receipt-btn"
          >
            Print Receipt
          </Button>
        </div>

        {/* Visit Lounge CTA */}
        <div className="mt-8 bg-stone-900 p-6 text-center relative overflow-hidden">
          <div className="absolute inset-0 opacity-10"
            style={{
              backgroundImage: `
                repeating-linear-gradient(45deg, transparent, transparent 20px, #fbbf24 20px, #fbbf24 22px),
                repeating-linear-gradient(-45deg, transparent, transparent 20px, #dc2626 20px, #dc2626 22px)
              `
            }}
          ></div>
          <div className="relative z-10">
            <Sparkles className="h-6 w-6 text-amber-400 mx-auto mb-3" />
            <h3 className="text-xl font-black text-white mb-2">Visit Our Lounge</h3>
            <p className="text-stone-400 mb-4">Experience Amlaria in person at our bohemian luxury showroom</p>
            <Link to="/lounge">
              <Button variant="outline" className="border-amber-500 text-amber-400 hover:bg-amber-500/10 rounded-none">
                Explore Lounge
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderSuccessPage;
